import React from 'react';
import PropTypes from 'prop-types';
import { generatePath } from 'react-router-dom';

import UserRole from '@careerstart/wae-common/src/main/constants/user-role';
import LanguageConverter from '@careerstart/wae-common/src/main/helperFunction/LanguageConverter';
import { Box, Card, CardContent, CardHeader, Divider } from '@mui/material';
import CardActions from '@mui/material/CardActions';
import Typography from '@mui/material/Typography';

import CopyLinkIcon from '../../../assets/icons/CopyLinkIcon';
import theme from '../../../theme';
import { FEED_BACK } from '../../../theme/colorConstants';
import { ROUTE_CONSTANTS } from '../../../views/app/RouteBuilding/routeConstants';
import TextLink from '../../TextLink/TextLink';
import { ROLE_ICONS } from '../constant';

import DualStatusHeader from './DualStatusHeader';

const styles = theme?.components?.userCard;

const RecruiterCard = ({ user, onCopyId, userDetailPath, userRole }) => {
  const agencyId = user?.recruiter?.agency?.ID;
  const agencyPath = agencyId
    ? generatePath(`/${ROUTE_CONSTANTS.DASHBOARD_AGENCY}/:id`, { id: agencyId })
    : undefined;
  const NON_CANDIDATE_STATUS_CONFIG = {
    active: {
      displayName: LanguageConverter('userCard.status.active'),
      color: FEED_BACK.GREEN_70,
      getIcon: (role) => ROLE_ICONS[role]?.active || ROLE_ICONS.recruiter.active,
    },
    deactivated: {
      displayName: LanguageConverter('userCard.status.deactivated'),
      color: FEED_BACK.RED_75,
      getIcon: (role) => ROLE_ICONS[role]?.deactivated || ROLE_ICONS.recruiter.deactivated,
    },
    inactive: {
      displayName: LanguageConverter('userCard.status.inactive'),
      color: FEED_BACK.RED_75,
      getIcon: (role) => ROLE_ICONS[role]?.inactive || ROLE_ICONS.recruiter.inactive,
    },
    unverified: {
      displayName: LanguageConverter('userCard.status.incomplete'),
      color: FEED_BACK.YELLOW_70,
      getIcon: (role) => ROLE_ICONS[role]?.unverified || ROLE_ICONS.recruiter.unverified,
    },
  };

  const currentStatus = NON_CANDIDATE_STATUS_CONFIG[user?.status];
  const statusIcon = currentStatus.getIcon(UserRole.RECRUITER);
  const statusV2Value = user?.state?.statusV2?.status;
  const statusV2 = NON_CANDIDATE_STATUS_CONFIG[statusV2Value];

  return (
    <Card sx={styles.cardStyle}>
      <CardHeader
        sx={styles.cardHeader}
        title={
          <DualStatusHeader
            showBoth={userRole === UserRole.ADMIN}
            statusV1={{
              icon: statusIcon,
              color: currentStatus?.color,
              displayName: currentStatus?.displayName,
            }}
            statusV2={{
              icon: statusV2?.getIcon(UserRole.RECRUITER),
              color: statusV2?.color,
              displayName: statusV2?.displayName,
              fallback: statusV2Value || '—',
            }}
          />
        }
      />

      <Divider />
      <CardContent>
        <Box display="flex" alignItems="center" gap={2}>
          <Box>
            <Typography sx={{ ...styles.workerTitle, textTransform: 'capitalize' }}>
              {user?.role}
            </Typography>
            <TextLink
              to={userRole === UserRole.ADMIN ? userDetailPath : undefined}
              sx={styles.roleName}
            >
              {user?.name}
            </TextLink>
            <Box
              display="flex"
              alignItems="center"
              onClick={onCopyId}
              sx={{ cursor: 'pointer', gap: 1 }}
            >
              <Typography sx={styles.copyUser}>
                {LanguageConverter('userCard.subTitle.copyUserId')}
              </Typography>
              <CopyLinkIcon />
            </Box>
          </Box>
        </Box>
        <Box mt={2}>
          <Box display="flex" alignItems="baseline" gap={1} sx={{ marginBottom: theme.spacing(2) }}>
            <Typography sx={styles.subTitleStyle}>
              {LanguageConverter('userCard.subTitle.email')}
            </Typography>
            <Typography sx={styles.emailStyle}>{user?.email}</Typography>
          </Box>
          {user?.recruiter?.agency?.name && (
            <Box display="flex" alignItems="baseline" gap={1}>
              <Typography sx={styles.corporationTitleStyle}>
                {LanguageConverter('userCard.subTitle.agency')}
              </Typography>
              <TextLink to={agencyPath} sx={{ ...styles.corporationStyle, cursor: 'pointer' }}>
                {user?.recruiter?.agency?.name}
              </TextLink>
            </Box>
          )}
        </Box>
      </CardContent>
      {user?.lastLoginDate && (
        <CardActions
          sx={{
            p: 2,
            justifyContent: 'flex-start',
            bottom: 0,
            width: '100%',
            mt: 'auto',
          }}
        >
          <Typography sx={styles.lastLoginHeader}>
            {LanguageConverter('userCard.subTitle.lastLoggedIn')}
          </Typography>
          <Typography sx={styles.lastLoginTime}>{user?.lastLoginDate}</Typography>
        </CardActions>
      )}
    </Card>
  );
};

RecruiterCard.propTypes = {
  user: PropTypes.shape({
    role: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    lastLoginDate: PropTypes.string,
    corporationName: PropTypes.string,
    state: PropTypes.shape({
      statusV2: PropTypes.shape({
        status: PropTypes.string,
      }),
    }),
    recruiter: PropTypes.shape({
      agency: PropTypes.shape({
        ID: PropTypes.string,
        name: PropTypes.string,
      }),
    }),
  }).isRequired,
  onCopyId: PropTypes.func,
  userDetailPath: PropTypes.string,
  userRole: PropTypes.string,
};

export default RecruiterCard;
